import { useState, useEffect, useMemo } from 'react'
import { FiSearch, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import api from '../utils/api'
import LojDetayModal from './LojDetayModal'

const formatNumber = (v) => {
  if (v == null || isNaN(v)) return ''
  return Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getTableSizes = () => {
  const col = (typeof localStorage !== 'undefined' && localStorage.getItem('tableColSize')) || 'md'
  const fnt = (typeof localStorage !== 'undefined' && localStorage.getItem('tableFontSize')) || 'md'
  return {
    padX: col === 'sm' ? 'px-1.5' : col === 'lg' ? 'px-4' : 'px-3',
    padY: col === 'sm' ? 'py-0.5' : col === 'lg' ? 'py-2.5' : 'py-1.5',
    font: fnt === 'sm' ? 'text-[10px]' : fnt === 'lg' ? 'text-sm' : 'text-xs',
  }
}

// Son tahsilattan gecen gun: uzadikca dikkat cekici renk
function gunRenk(g) {
  if (g == null) return 'text-gray-400'
  if (g >= 30) return 'text-red-600 font-medium'
  if (g >= 15) return 'text-orange-600'
  if (g >= 7) return 'text-amber-600'
  return 'text-gray-600 dark:text-gray-300'
}

export default function BakiyeWidget({ datasourceId = 6, sube = null }) {
  const [allData, setAllData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortCol, setSortCol] = useState('BAKIYE')
  const [sortDir, setSortDir] = useState('desc')
  const [selectedBlcari, setSelectedBlcari] = useState(null)
  // Baska widget'tan (Son Cari Hareketler vb.) gelen filtre UNVAN bazli tutulur:
  // BLCARI her istasyon DB'sinde FARKLI oldugu icin (bakiye hep ds=6'dan yuklenir,
  // hareket tiklamasi ise secili subenin BLCARI'sini tasir) BLCARI eslesmesi bos tablo veriyordu.
  const [filterUnvan, setFilterUnvan] = useState(null)
  const [tarihBas, setTarihBas] = useState('')
  const [tarihBit, setTarihBit] = useState('')
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 25

  useEffect(() => { loadAll() }, [datasourceId, sube])

  // Panel kapanirsa secimi ve filtre kaldir
  useEffect(() => {
    const h = () => { setSelectedBlcari(null); setFilterUnvan(null) }
    window.addEventListener('cari-detay-close', h)
    return () => window.removeEventListener('cari-detay-close', h)
  }, [])

  // Sadece DIS widget'lardan (Son Cari Hareketler vb.) gelen event'lerde UNVAN filtresi uygula.
  // Kendi handleRowClick'imiz source='bakiye' isaretiyle gelir, atlanir.
  useEffect(() => {
    const norm = (s) => String(s || '').trim().toLocaleUpperCase('tr-TR')
    const onSelected = (e) => {
      const { UNVAN, source } = e.detail || {}
      if (!UNVAN) return
      if (source === 'bakiye') return  // kendi tiklamamiz - sadece CariDetayPanel acilsin
      setFilterUnvan(prev => {
        if (prev && norm(prev) === norm(UNVAN)) {
          setSearch('')
          setPage(0)
          setSelectedBlcari(null)
          window.dispatchEvent(new CustomEvent('cari-detay-close'))
          return null
        }
        setSearch('')
        setPage(0)
        return UNVAN
      })
    }
    window.addEventListener('cari-selected', onSelected)
    return () => window.removeEventListener('cari-selected', onSelected)
  }, [])

  const loadAll = async () => {
    setLoading(true)
    try {
      // Poçan "tüm şubeler" -> 3 istasyon birleşik (VKN-merge); tek şube -> o istasyon.
      const _params = sube ? { sube } : { datasource_id: datasourceId }
      const { data } = await api.get('/bakiye-full', { params: _params })
      setAllData(data)
    } catch { }
    setLoading(false)
  }

  // Bakiye tablosu - tarih filtreli
  const bakiyeData = useMemo(() => {
    if (!allData?.hareketler) return allData?.bakiye || []
    if (!tarihBas && !tarihBit) return allData?.bakiye || []
    const filtered = allData.hareketler.filter(h => {
      const t = String(h.TARIH || '').substring(0, 10)
      if (tarihBas && t < tarihBas) return false
      if (tarihBit && t > tarihBit) return false
      return true
    })
    const map = {}
    filtered.forEach(h => {
      const key = h.BLCARI
      if (!map[key]) map[key] = { BLCARI: key, UNVAN: '', BORC: 0, ALACAK: 0 }
      map[key].BORC += (h.BORC || 0)
      map[key].ALACAK += (h.ALACAK || 0)
    })
    ;(allData.bakiye || []).forEach(b => { if (map[b.BLCARI]) map[b.BLCARI].UNVAN = b.UNVAN })
    return Object.values(map).map(r => ({ ...r, BAKIYE: r.BORC - r.ALACAK })).sort((a, b) => b.BAKIYE - a.BAKIYE)
  }, [allData, tarihBas, tarihBit])

  const displayed = useMemo(() => {
    let d = bakiyeData
    if (filterUnvan != null) {
      const norm = (s) => String(s || '').trim().toLocaleUpperCase('tr-TR')
      const q = norm(filterUnvan)
      d = d.filter(r => norm(r.UNVAN) === q)
    } else if (search) {
      const q = search.toLowerCase()
      d = d.filter(r => (r.UNVAN || '').toLowerCase().includes(q))
    }
    return [...d].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol]
      // Tahsilati hic olmayan cariler (null) her zaman en sona
      if (sortCol === 'SON_TAHSILAT_GUN') {
        if (va == null && vb == null) return 0
        if (va == null) return 1
        if (vb == null) return -1
        return sortDir === 'asc' ? va - vb : vb - va
      }
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va
      return sortDir === 'asc' ? String(va || '').localeCompare(String(vb || ''), 'tr') : String(vb || '').localeCompare(String(va || ''), 'tr')
    })
  }, [bakiyeData, search, sortCol, sortDir, filterUnvan])

  // Filtre sonrasi TUM satirlarin toplami (tfoot'ta gosterilir)
  const toplam = useMemo(() => displayed.reduce((a, r) => {
    a.BORC += r.BORC || 0; a.ALACAK += r.ALACAK || 0; a.BAKIYE += r.BAKIYE || 0
    return a
  }, { BORC: 0, ALACAK: 0, BAKIYE: 0 }), [displayed])

  const totalPages = Math.ceil(displayed.length / PAGE_SIZE)
  const paged = displayed.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  // Satira tikla: 3-istasyon cari detay MODALI (alt panel/filtre yok — kullanici karari)
  const [modalCari, setModalCari] = useState(null)
  const handleRowClick = (r) => {
    if (r.UNVAN) setModalCari(r.UNVAN)
  }

  if (loading) return <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">Yukleniyor...</div>

  const sz = getTableSizes()

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - arama */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex-shrink-0">
        <div className="relative flex-1">
          <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" size={12} />
          <input value={search} onChange={e => { setSearch(e.target.value); setFilterBlcari(null); setPage(0) }}
            placeholder="Cari ara..." className="w-full pl-7 pr-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100" />
        </div>
        {filterUnvan && (
          <button onClick={() => { setFilterUnvan(null); setSelectedBlcari(null); setPage(0) }}
            title="Filtreyi temizle"
            className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 hover:bg-red-100 hover:text-red-600 truncate max-w-[180px]">
            {filterUnvan} ✕
          </button>
        )}
        <span className="text-[10px] text-gray-400">{displayed.length} cari</span>
      </div>

      {/* Bakiye Tablosu */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className={`w-full ${sz.font}`}>
          <thead className="sticky top-0 z-20">
            <tr className="bg-gray-100 dark:bg-gray-700">
              {[['UNVAN','Unvan','text-left'],['SON_TAHSILAT_GUN','Son Tahsilat','text-right'],['BORC','Borc','text-right'],['ALACAK','Alacak','text-right'],['BAKIYE','Bakiye','text-right']].map(([k,l,a], colIdx) => (
                <th key={k} className={`${a} ${sz.padX} py-2 font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 select-none ${colIdx === 0 ? 'sticky left-0 z-30 bg-gray-100 dark:bg-gray-700 shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]' : ''}`}
                  onClick={() => { setSortCol(k); setSortDir(d => sortCol === k ? (d === 'asc' ? 'desc' : 'asc') : 'desc') }}>
                  {l} {sortCol === k && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 && filterUnvan && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400 text-xs">
                "{filterUnvan}" bakiye listesinde bulunamadı — filtreyi ✕ ile temizleyebilirsiniz
              </td></tr>
            )}
            {paged.map((r, i) => {
              const rowBg = selectedBlcari === r.BLCARI ? 'bg-primary-50 dark:bg-primary-900/30' : 'bg-white dark:bg-gray-800'
              return (
              <tr key={i} data-blcari={r.BLCARI} onClick={() => handleRowClick(r)}
                className={`border-b dark:border-gray-700 cursor-pointer ${selectedBlcari === r.BLCARI ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-200 dark:border-primary-800' : 'hover:bg-blue-50/50 dark:hover:bg-blue-900/20'}`}>
                <td className={`${sz.padX} ${sz.padY} sticky left-0 z-[1] ${rowBg} shadow-[2px_0_3px_-1px_rgba(0,0,0,0.06)]`}>{r.UNVAN}</td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums ${gunRenk(r.SON_TAHSILAT_GUN)}`}
                  title={r.SON_TAHSILAT ? `Son tahsilat: ${r.SON_TAHSILAT}` : 'Tahsilat kaydi yok'}>
                  {r.SON_TAHSILAT_GUN == null ? '-' : r.SON_TAHSILAT_GUN === 0 ? 'Bugun' : `${r.SON_TAHSILAT_GUN} gun`}
                </td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums text-red-600`}>{formatNumber(r.BORC)}</td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums text-green-600`}>{formatNumber(r.ALACAK)}</td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums font-medium ${r.BAKIYE > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>{formatNumber(r.BAKIYE)}</td>
              </tr>
            )})}
          </tbody>
          {/* TOPLAM: gorunen sayfanin degil, filtre sonrasi TUM carilerin toplami */}
          {displayed.length > 0 && (
            <tfoot className="sticky bottom-0 z-20">
              <tr className="bg-gray-200/95 dark:bg-gray-700/95 backdrop-blur border-t-2 border-gray-400 dark:border-gray-500 font-semibold shadow-[0_-2px_4px_-2px_rgba(0,0,0,0.15)]">
                <td className={`${sz.padX} ${sz.padY} sticky left-0 z-30 bg-gray-200/95 dark:bg-gray-700/95 backdrop-blur whitespace-nowrap`}>
                  TOPLAM <span className="font-normal text-gray-500 dark:text-gray-400">({displayed.length} cari)</span>
                </td>
                <td className={`${sz.padX} ${sz.padY}`}></td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums text-red-600`}>{formatNumber(toplam.BORC)}</td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums text-green-600`}>{formatNumber(toplam.ALACAK)}</td>
                <td className={`${sz.padX} ${sz.padY} text-right tabular-nums ${toplam.BAKIYE > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>{formatNumber(toplam.BAKIYE)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex-shrink-0">
          <div className="flex items-center justify-between px-3 py-1 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-xs text-gray-500 dark:text-gray-400">
            <span>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, displayed.length)} / {displayed.length}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30"><FiChevronLeft size={14} /></button>
              <span>{page + 1} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-30"><FiChevronRight size={14} /></button>
            </div>
          </div>
        </div>
      )}
      {modalCari && <LojDetayModal type="petrol-cari" value={modalCari} onClose={() => setModalCari(null)} />}
    </div>
  )
}
